import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { RiderService, Rider } from '../../../services/riders/rider.service';
import { SnackbarService } from '../../../services/design/snackbar.service';

import { RiderVerificationDetailComponent } from '../rider-verification/rider-verification-detail.component';

@Component({
  selector: 'app-riders-list',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, RiderVerificationDetailComponent],
  templateUrl: './riders-list.component.html',
  styleUrls: ['./riders-list.component.scss'],
})
export class AllRidersComponent implements OnInit {
  private riderService = inject(RiderService);
  private snackbar = inject(SnackbarService);

  riders = signal<Rider[]>([]);
  isLoading = signal<boolean>(false);
  selectedRider = signal<Rider | null>(null);
  showVerificationModal = signal<boolean>(false);
  searchQuery = signal<string>('');
  totalRiders = signal(0);
  fleetStats = signal({ total: 0, online: 0, pending: 0 });
  private searchTimer?: ReturnType<typeof setTimeout>;

  /** Pagination */
  currentPage = 1;
  pageSize = 10;

  /** Tabs */
  activeStatus: 'All' | 'Online' | 'Offline' | 'Busy' | 'PENDING' | 'REJECTED' = 'All';

  Math = Math;
  min = Math.min;

  get onlineRiderCount() {
    return this.fleetStats().online;
  }

  get pendingRiderCount() {
    return this.fleetStats().pending;
  }

  statusTabs: ('All' | 'Online' | 'Offline' | 'Busy' | 'PENDING' | 'REJECTED')[] = 
    ['All', 'Online', 'Offline', 'Busy', 'PENDING', 'REJECTED'];

  getVehicleIcon(type = ''): string {
    const t = type?.toLowerCase() || '';
    if (t.includes('bike')) return 'fa-motorcycle';
    if (t.includes('cycle')) return 'fa-bicycle';
    if (t.includes('car')) return 'fa-car';
    if (t.includes('truck')) return 'fa-truck';
    return 'fa-van-shuttle';
  }

  ngOnInit(): void {
    this.loadRiders();
  }

  loadRiders() {
    this.isLoading.set(true);
    this.riderService.getRidersPage({
      page: this.currentPage,
      pageSize: this.pageSize,
      status: this.activeStatus,
      search: this.searchQuery(),
    }).subscribe({
      next: (result) => {
        this.riders.set(result.data);
        this.totalRiders.set(result.total);
        this.fleetStats.set(result.stats);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.snackbar.error('Failed to load riders');
        this.isLoading.set(false);
      }
    });
  }

  get filteredRiders() {
    return this.riders();
  }

  /** Paginated Riders */
  get paginatedRiders() {
    return this.riders();
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.totalRiders() / this.pageSize));
  }

  changeStatus(status: 'All' | 'Online' | 'Offline' | 'Busy' | 'PENDING' | 'REJECTED') {
    this.activeStatus = status;
    this.currentPage = 1;
    this.loadRiders();
  }

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
    this.currentPage = 1;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadRiders(), 300);
  }

  async toggleAvailability(rider: Rider) {
      const isOnline = !(rider.presence?.is_online);
      this.riderService.updateRiderAvailability(rider.id, isOnline).subscribe({
          next: () => {
              this.snackbar.success(`Rider ${rider.full_name} is now ${isOnline ? 'Online' : 'Offline'}`);
              this.loadRiders();
          },
          error: () => this.snackbar.error('Failed to update status')
      });
  }

  viewRiderDetail(rider: Rider) {
    this.selectedRider.set(rider);
    this.showVerificationModal.set(true);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadRiders();
    }
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadRiders();
    }
  }
}
